// src/context/BrandingContext.tsx
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchPublicBranding } from '../api/branding/brandingApi';
import { Branding, DEFAULT_BRANDING, normalizeBranding } from '../types/branding/BrandingTypes';
import { applyBranding } from '../utils/branding/applyBranding';

/**
 * S6 (86d3w1g8u) — the single answer to "what does this clinic look like".
 *
 * *** THE PROVIDER MUST SIT OUTSIDE EVERY AUTH GUARD. ***
 *
 * `/login` and `/patient/booking-form` need branding and have no session, so this is
 * mounted in `App.tsx` above `SettingsProvider`, outside every `PrivateRoute`, and
 * outside anything that reads the `user` cookie — but INSIDE `QueryClientProvider`,
 * because it uses `useQuery`. Mounting it any deeper FAILS SILENTLY: the staff shell
 * brands correctly and the login page does not.
 *
 * *** IT FAILS SOFT, ALWAYS. ***
 *
 * A 500, a timeout, a 502 HTML body and a malformed payload all resolve to
 * `DEFAULT_BRANDING` with `isResolved: true` — never a toast, never an error boundary.
 * A clinic whose auth service is briefly unreachable must still render a usable login
 * page, and "unreachable" must be indistinguishable on screen from "not entitled".
 */

export interface BrandingContextValue {
	/** Never undefined, never partial — see `normalizeBranding`. */
	branding: Branding;
	/**
	 * Has the fetch settled (either way)? While false, `BrandLogo` reserves its height
	 * and renders nothing, so there is no Xoe-Med-then-clinic-logo flash.
	 */
	isResolved: boolean;
	/** Invalidate the cached payload so the app re-brands without a reload (S9). */
	refresh: () => void;
}

export const BRANDING_QUERY_KEY = ['public-branding'] as const;

/**
 * The value returned when a consumer renders OUTSIDE the provider.
 *
 * `isResolved: true` is deliberate: with no provider there is nothing to wait for, so a
 * `BrandLogo` in an isolated unit test — or in some future tree that forgets the
 * provider — renders the Xoe Med lockup, which is exactly today's appearance, rather
 * than an invisible placeholder forever. It is the same fail-soft rule as everywhere
 * else in this file.
 */
const OUTSIDE_PROVIDER: BrandingContextValue = {
	branding: DEFAULT_BRANDING,
	isResolved: true,
	refresh: () => undefined,
};

const BrandingContext = createContext<BrandingContextValue>(OUTSIDE_PROVIDER);

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const queryClient = useQueryClient();

	// One request per page load. `staleTime` keeps a remount (e.g. navigating from the
	// login page into the shell) off the wire, and the endpoint is itself cached 300 s
	// server-side. `retry: 1` matches the app-wide default; a slow clinic auth service
	// costs one extra attempt, not a stalled boot.
	const { data, isPending } = useQuery({
		queryKey: BRANDING_QUERY_KEY,
		queryFn: fetchPublicBranding,
		staleTime: 5 * 60 * 1000,
		retry: 1,
	});

	const value = useMemo<BrandingContextValue>(
		() => ({
			// ⚠️ Merge over the defaults rather than trusting the payload shape. `data` is
			// whatever came back on the wire; a future backend change, a proxy error page or
			// a partial body must not be able to produce `undefined.length` in a component.
			branding: data === undefined ? DEFAULT_BRANDING : normalizeBranding(data),
			// Settled either way. On error `data` stays undefined and we fall through to
			// DEFAULT_BRANDING above — the error itself is never surfaced.
			isResolved: !isPending,
			refresh: () => {
				void queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
			},
		}),
		[data, isPending, queryClient],
	);

	// S7 — hand the resolved branding to the document appliers: tab title, favicon, and the
	// seven `--mg-primary*` custom properties.
	//
	// ⚠️ This is the FIRST application, not the only one. `applyFullTheme()` rewrites the same
	// seven properties on every preference change, so the four call sites in
	// `useUserPreferences.tsx` and the one in `App.tsx` each re-assert the brand colour after
	// their await. This effect covers the opposite race: branding arriving AFTER the theme has
	// already been applied on boot.
	//
	// Runs on `isResolved` too, so a failed fetch still lands DEFAULT_BRANDING — which restores
	// today's exact appearance rather than leaving whatever a previous clinic's payload wrote.
	useEffect(() => {
		if (!value.isResolved) return;
		applyBranding(value.branding);
	}, [value.isResolved, value.branding]);

	return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

/** Read the clinic's branding. Safe outside the provider — see `OUTSIDE_PROVIDER`. */
export const useBranding = (): BrandingContextValue => useContext(BrandingContext);

export default BrandingContext;
